/**
 * Plays a grid sprite sheet as a looping animation, web only.
 *
 * ## How the loop works
 *
 * The sheet is `cols × rows` frames read left-to-right, top-to-bottom. We show
 * one cell at a time through a viewport the size of a single frame, and walk
 * `background-position` across the sheet with a CSS `steps()` animation:
 *
 *   - the background image is scaled so ONE cell is exactly `size × size` px
 *     (i.e. the whole sheet is `cols*size × rows*size`);
 *   - a keyframe animation steps background-position-x from 0 to `-(cols*size)px`
 *     across a row, then a second (slower) one steps position-y down the rows.
 *
 * ## Pixel offsets, not percentages
 *
 * This uses pixel offsets deliberately. CSS `background-position` percentages are
 * relative to (container − image), which spans only `cols−1` cells, so a
 * `0%→100%` walk with `steps(cols)` lands on fractional offsets and every cell
 * after the first is misaligned — two half-frames bleed into the viewport. Pixel
 * offsets (`0 → -(cols*size)`) put each step exactly on a cell boundary. Verified
 * frame-by-frame against a real generated sheet.
 *
 * `steps(n)` is what makes it read as distinct frames rather than a smooth slide:
 * the position jumps cell-to-cell with no interpolation, so each frame is held
 * for its full slice of the duration.
 *
 * ## Why CSS steps() rather than a canvas/JS loop
 *
 * No per-frame JavaScript, no requestAnimationFrame to manage, and the browser
 * composites it — so it keeps ticking smoothly even while the main thread is busy
 * (e.g. the next generation request). The whole animation is two @keyframes and a
 * bit of arithmetic.
 *
 * ## Why web-only
 *
 * It relies on CSS background-position stepping and injected @keyframes. The rest
 * of the app runs on React Native Web for the browser and Metro for Android;
 * this page is a browser toy, so on native it just renders a note.
 */

import React, { useMemo } from "react";
import { View, Text, Platform, StyleSheet } from "react-native";
import { colors, textStyle } from "../styles";

interface Props {
  /** PNG sprite sheet as a base64 payload (no data: prefix). */
  imageBase64: string;
  cols: number;
  rows: number;
  /** Full loop duration in seconds across all frames. */
  durationSec?: number;
  /** Rendered size of the square viewport, in px. */
  size?: number;
}

/**
 * A module-level counter gives each mounted player a unique keyframe name.
 * Two players on one page with the same @keyframes name would fight over it;
 * a per-instance name keeps their row/column timing independent.
 */
let instanceSeq = 0;

export default function SpriteSheetPlayer({
  imageBase64,
  cols,
  rows,
  durationSec = 2,
  size = 320,
}: Props) {
  // Stable per-mount id for this instance's injected keyframes.
  const uid = useMemo(() => `sheet-${instanceSeq++}`, []);

  if (Platform.OS !== "web") {
    return (
      <View style={[styles.fallback, { width: size, height: size }]}>
        <Text style={styles.fallbackText}>The animation plays in the web app.</Text>
      </View>
    );
  }

  const frames = cols * rows;
  const src = `data:image/png;base64,${imageBase64}`;

  // Column walk: one row's worth of frames per pass. Row walk: down the rows.
  // Splitting X and Y timing this way is what turns a 2-D grid into a single
  // left-to-right, top-to-bottom sequence: X cycles fast (once per row), Y steps
  // once each time X completes a full row.
  const rowDurationSec = durationSec / rows;

  // Pixel offsets so each step lands exactly on a cell boundary (see the header
  // comment on why percentages don't). The end offset is -(cols*size) so the
  // last step of the row scrolls the final column fully out and wraps to 0.
  const keyframes = `
@keyframes ${uid}-x {
  from { background-position-x: 0px; }
  to   { background-position-x: -${cols * size}px; }
}
@keyframes ${uid}-y {
  from { background-position-y: 0px; }
  to   { background-position-y: -${rows * size}px; }
}`;

  const animStyle: React.CSSProperties = {
    width: size,
    height: size,
    backgroundImage: `url("${src}")`,
    backgroundRepeat: "no-repeat",
    // One cell is exactly size×size px, so the whole sheet is cols*size × rows*size.
    backgroundSize: `${cols * size}px ${rows * size}px`,
    imageRendering: "auto",
    borderRadius: 12,
    // Two animations run together: X steps across a row, Y steps down the rows.
    // steps(n) holds each cell for its full slice — that's what makes it a
    // frame-by-frame loop instead of a continuous pan.
    animation: `${uid}-x ${rowDurationSec}s steps(${cols}) infinite, ${uid}-y ${durationSec}s steps(${rows}) infinite`,
  };

  return (
    <View style={[styles.wrap, { width: size, height: size }]}>
      {/* Keyframes must live in the document; scoped by the per-instance uid. */}
      <style dangerouslySetInnerHTML={{ __html: keyframes }} />
      <div style={animStyle} aria-label={`Looping animation, ${frames} frames`} />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    backgroundColor: colors.surfaceDark,
    borderRadius: 12,
    overflow: "hidden",
  },
  fallback: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surfaceDark,
    borderRadius: 12,
  },
  fallbackText: { color: colors.textMuted, ...textStyle.body, textAlign: "center", padding: 16 },
});
